import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useForm } from '@inertiajs/react'
import { CheckCircle2, FlaskConical, Loader2, Play, RefreshCw, XCircle } from 'lucide-react'
import { PanelEmptyState } from '@/frontend/components/EmptyState'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import type { EvalRunRow, EvalRunsData, EvalRunStatus } from '@/frontend/pages/Dashboard/DigitalWorkers/types'

interface Props {
  data: EvalRunsData | null
  workerId: string
  canManage: boolean
}

const STATUS_LABELS: Record<EvalRunStatus, string> = {
  queued: 'Na fila',
  running: 'Rodando',
  succeeded: 'Concluída',
  failed: 'Falhou',
}

function toLocalInput(value: string | null | undefined): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
  ].join('-') + `T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function formatDate(value: string | null): string {
  if (!value) return 'Sem filtro'
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value))
}

function statusVariant(status: EvalRunStatus): 'success' | 'critical' | 'warning' | 'info' {
  if (status === 'succeeded') return 'success'
  if (status === 'failed') return 'critical'
  if (status === 'running') return 'warning'
  return 'info'
}

function StatusIcon({ status }: { status: EvalRunStatus }) {
  if (status === 'succeeded') return <CheckCircle2 className="h-4 w-4 text-success" />
  if (status === 'failed') return <XCircle className="h-4 w-4 text-destructive" />
  if (status === 'running') return <Loader2 className="h-4 w-4 animate-spin text-warning" />
  return <Loader2 className="h-4 w-4 text-muted-foreground" />
}

function runHeadline(run: EvalRunRow): string {
  return run.summary.primary_verdict || run.summary.primary_category || run.status
}

function fleetJobLabel(run: EvalRunRow): string {
  if (run.broker_job_id) return run.broker_job_id
  if (run.origin === 'external_artifact') return 'não se aplica'
  return 'pendente'
}

type JsonRecord = Record<string, unknown>

interface ArtifactText {
  chars?: number
  content: string
  path?: string
  truncated?: boolean
}

interface ArtifactTextChunk {
  chars?: number
  complete?: boolean
  content?: string
  nextOffset?: number
}

function isRecord(value: unknown): value is JsonRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function recordValue(value: unknown): JsonRecord | null {
  return isRecord(value) ? value : null
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null
}

function numberValue(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function artifactTextChunkUrl(workerId: string, runId: string, kind: 'rubric' | 'judgePrompt', offset: number): string {
  const params = new URLSearchParams({
    kind,
    offset: String(offset),
    max_chars: '6000',
  })
  return `/dashboard/digital_workers/${workerId}/eval_runs/${runId}/artifact_text?${params.toString()}`
}

function artifactText(run: EvalRunRow, key: 'rubric' | 'judgePrompt'): ArtifactText | null {
  const texts = recordValue(run.artifact_metadata.texts)
  const text = recordValue(texts?.[key])
  const content = stringValue(text?.content)
  if (!content) return null

  return {
    chars: typeof text?.chars === 'number' ? text.chars : undefined,
    content,
    path: stringValue(text?.path) ?? undefined,
    truncated: text?.truncated === true,
  }
}

function evaluationRows(run: EvalRunRow): JsonRecord[] {
  const results = run.result.results
  if (Array.isArray(results)) return results.filter(isRecord)
  return Object.keys(run.result).length > 0 ? [run.result] : []
}

function primaryJudge(row: JsonRecord | null): JsonRecord | null {
  if (!row) return null
  return recordValue(row.llmJudge) || recordValue(row.sessionJudge) || row
}

function formatUnknown(value: unknown): string {
  if (typeof value === 'string') return value
  return JSON.stringify(value, null, 2)
}

function TextArtifactDetails({
  kind,
  label,
  runId,
  text,
  workerId,
}: {
  kind: 'rubric' | 'judgePrompt'
  label: string
  runId: string
  text: ArtifactText | null
  workerId: string
}) {
  const [fullContent, setFullContent] = useState<string | null>(null)
  const [fullChars, setFullChars] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setFullContent(null)
    setFullChars(null)
    setError(null)
  }, [text?.path])

  if (!text) return null

  const visibleContent = fullContent ?? text.content
  const visibleChars = fullChars ?? text.chars
  const isLimited = text.truncated === true && fullContent === null

  const loadFullText = async () => {
    setLoading(true)
    setError(null)
    let offset = 0
    let content = ''
    try {
      for (let chunkIndex = 0; chunkIndex < 50; chunkIndex += 1) {
        const response = await fetch(artifactTextChunkUrl(workerId, runId, kind, offset), {
          headers: { Accept: 'application/json' },
        })
        const payload = await response.json()
        if (!response.ok) {
          throw new Error(String(payload.error || 'Não foi possível carregar o arquivo completo.'))
        }
        const chunk = payload as ArtifactTextChunk
        content += typeof chunk.content === 'string' ? chunk.content : ''
        setFullContent(content)
        const chars = numberValue(chunk.chars)
        if (chars !== null) setFullChars(chars)
        if (chunk.complete === true) return
        const nextOffset = numberValue(chunk.nextOffset)
        if (nextOffset === null || nextOffset <= offset) {
          throw new Error('Resposta de artifact sem próximo offset válido.')
        }
        offset = nextOffset
      }
      throw new Error('Arquivo excede o limite de chunks da UI.')
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : String(loadError))
    } finally {
      setLoading(false)
    }
  }

  return (
    <details className="rounded-md border border-border bg-muted/20 p-3">
      <summary className="cursor-pointer text-sm font-medium">{label}</summary>
      {isLimited && (
        <p className="mt-2 text-xs text-warning">
          Conteúdo limitado na sincronização{text.chars ? `; tamanho original ${text.chars} caracteres` : ''}.
        </p>
      )}
      {text.path && (
        <p className="mt-2 break-all text-xs text-muted-foreground">Arquivo completo: {text.path}</p>
      )}
      {isLimited && (
        <Button type="button" variant="outline" size="sm" className="mt-3" onClick={loadFullText} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {loading ? 'Carregando completo...' : 'Carregar completo'}
        </Button>
      )}
      {fullContent !== null && (
        <p className="mt-2 text-xs text-success">
          Conteúdo completo carregado{visibleChars ? `; ${visibleChars} caracteres` : ''}.
        </p>
      )}
      {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
      <pre className="mt-3 max-h-[32rem] overflow-auto whitespace-pre-wrap break-words text-xs leading-relaxed">{visibleContent}</pre>
    </details>
  )
}

function EvidenceBlock({ value }: { value: unknown }) {
  if (value === undefined || value === null) return null
  const items = Array.isArray(value) ? value : [value]
  if (items.length === 0) return null

  return (
    <div className="rounded-md border border-border bg-muted/20 p-3">
      <div className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-faint-foreground">Evidências</div>
      <div className="mt-2 space-y-2">
        {items.map((item, index) => (
          <pre key={index} className="whitespace-pre-wrap break-words text-xs leading-relaxed">{formatUnknown(item)}</pre>
        ))}
      </div>
    </div>
  )
}

function EvaluationDetails({ run }: { run: EvalRunRow }) {
  const rows = evaluationRows(run)
  const row = rows[0] ?? null
  const judge = primaryJudge(row)
  const verdict = stringValue(judge?.verdict) || run.summary.primary_verdict || null
  const category = stringValue(row?.category) || run.summary.primary_category || null
  const confidence = stringValue(judge?.confidence) || stringValue(row?.confidence)
  const model = stringValue(judge?.model) || run.judge_model
  const reason = stringValue(judge?.reason) || stringValue(row?.reason)
  const evidence = judge?.evidence ?? row?.evidence
  const fields = [
    ['Veredito', verdict],
    ['Categoria', category],
    ['Confiança', confidence],
    ['Modelo', model || 'padrão do OpenClaw'],
  ].filter(([, value]) => Boolean(value))

  if (!row && fields.length === 0 && !reason) return null

  return (
    <div className="space-y-3 rounded-md border border-border bg-background p-3">
      <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-4">
        {fields.map(([label, value]) => (
          <div key={label} className="min-w-0">
            <div className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-faint-foreground">{label}</div>
            <div className="mt-1 break-words font-medium">{value}</div>
          </div>
        ))}
      </div>
      {reason && (
        <div>
          <div className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-faint-foreground">Razão do judge</div>
          <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-relaxed">{reason}</p>
        </div>
      )}
      <EvidenceBlock value={evidence} />
      {rows.length > 1 && (
        <div className="text-xs text-muted-foreground">
          Exibindo a primeira de {rows.length} sessões sincronizadas
          {run.summary.count && run.summary.count > rows.length ? `; o artifact completo contém ${run.summary.count}.` : '.'}
        </div>
      )}
    </div>
  )
}

function RunCard({ run, workerId }: { run: EvalRunRow; workerId: string }) {
  const hasOutput = Object.keys(run.result || {}).length > 0
  const rubric = artifactText(run, 'rubric')
  const judgePrompt = artifactText(run, 'judgePrompt')
  const createdAtLabel = run.origin === 'external_artifact' ? 'Importada em' : 'Criada em'
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2 text-[14px] font-semibold">
              <StatusIcon status={run.status} />
              {runHeadline(run)}
            </CardTitle>
            <CardDescription className="text-[13px]">
              <span className="font-mono text-[11.5px]">{formatDate(run.from_at)}</span> até{' '}
              <span className="font-mono text-[11.5px]">{formatDate(run.to_at)}</span> · agente {run.agent_id}
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant={statusVariant(run.status)}>{STATUS_LABELS[run.status]}</Badge>
            {run.origin === 'external_artifact' && <Badge variant="outline">Artifact externo</Badge>}
            {run.summary.count !== undefined && <Badge variant="outline">{run.summary.count} sessão{run.summary.count === 1 ? '' : 'ões'}</Badge>}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {run.error_message && (
          <Alert variant="destructive">
            <AlertTitle>Erro</AlertTitle>
            <AlertDescription>{run.error_message}</AlertDescription>
          </Alert>
        )}
        {run.result_parse_error && (
          <Alert variant="warning">
            <AlertTitle>Saída truncada</AlertTitle>
            <AlertDescription>{run.result_parse_error}</AlertDescription>
          </Alert>
        )}
        <div className="grid grid-cols-1 gap-2 text-xs text-muted-foreground md:grid-cols-2">
          <div>Job Fleet: {fleetJobLabel(run)}</div>
          <div>Modelo: {run.judge_model || 'padrão do OpenClaw'}</div>
          <div>{createdAtLabel}: {formatDate(run.created_at)}</div>
          <div>Finalizada em: {formatDate(run.completed_at)}</div>
          <div>Origem: {run.origin === 'external_artifact' ? 'artifact externo' : 'Workstation'}</div>
          <div>Sincronizada em: {formatDate(run.synced_at)}</div>
          {run.external_artifact_path && (
            <div className="break-all md:col-span-2">Artifact: {run.external_artifact_path}</div>
          )}
        </div>
        {run.judge_request && (
          <div className="rounded-md border border-border bg-muted/20 p-3">
            <div className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-faint-foreground">Pedido original</div>
            <p className="mt-1 whitespace-pre-wrap break-words text-sm">{run.judge_request}</p>
          </div>
        )}
        <EvaluationDetails run={run} />
        <div className="space-y-3">
          <TextArtifactDetails kind="rubric" label="Rubrica efetiva" runId={run.id} text={rubric} workerId={workerId} />
          <TextArtifactDetails kind="judgePrompt" label="Prompt enviado ao LLM judge" runId={run.id} text={judgePrompt} workerId={workerId} />
        </div>
        {hasOutput && (
          <details className="rounded-md border border-border bg-muted/30 p-3">
            <summary className="cursor-pointer text-sm font-medium">JSON bruto da avaliação</summary>
            <pre className="mt-3 max-h-96 overflow-auto text-xs">{JSON.stringify(run.result, null, 2)}</pre>
          </details>
        )}
        {(run.stdout_tail || run.stderr_tail) && (
          <details className="rounded-md border border-border bg-muted/30 p-3">
            <summary className="cursor-pointer text-sm font-medium">Logs do job</summary>
            {run.stdout_tail && <pre className="mt-3 max-h-48 overflow-auto text-xs">{run.stdout_tail}</pre>}
            {run.stderr_tail && <pre className="mt-3 max-h-48 overflow-auto text-xs text-destructive">{run.stderr_tail}</pre>}
          </details>
        )}
      </CardContent>
    </Card>
  )
}

export default function EvalsTab({ data, workerId, canManage }: Props) {
  const [evalData, setEvalData] = useState<EvalRunsData | null>(data)
  const [refreshing, setRefreshing] = useState(false)
  const [refreshError, setRefreshError] = useState<string | null>(null)
  const form = useForm({
    agent_id: data?.defaults.agent_id ?? 'main',
    judge_request: data?.defaults.judge_request ?? 'me fale se essa sessão teve bom uso da memória',
    judge_model: '',
    from_at: toLocalInput(data?.defaults.from_at),
    to_at: toLocalInput(data?.defaults.to_at),
  })

  useEffect(() => {
    setEvalData(data)
  }, [data])

  const refreshEvalRuns = useCallback(async (showSpinner = false) => {
    if (showSpinner) setRefreshing(true)
    setRefreshError(null)
    try {
      const response = await fetch(`/dashboard/digital_workers/${workerId}/eval_runs`, {
        headers: { Accept: 'application/json' },
      })
      const payload = await response.json()
      if (!response.ok) {
        throw new Error(String(payload.error || 'Não foi possível atualizar as evals.'))
      }
      setEvalData(payload as EvalRunsData)
    } catch (error) {
      setRefreshError(error instanceof Error ? error.message : String(error))
    } finally {
      if (showSpinner) setRefreshing(false)
    }
  }, [workerId])

  const runs = evalData?.runs ?? []
  const hasActiveRun = runs.some(run => run.status === 'queued' || run.status === 'running')
  const shouldPollInitialSync = evalData?.sync.pending === true && runs.length === 0
  const shouldPoll = hasActiveRun || shouldPollInitialSync

  useEffect(() => {
    if (!shouldPoll) return
    const timeout = setTimeout(() => { void refreshEvalRuns(false) }, 5000)
    return () => clearTimeout(timeout)
  }, [refreshEvalRuns, shouldPoll])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    form.post(`/dashboard/digital_workers/${workerId}/eval_runs`, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => { void refreshEvalRuns(false) },
    })
  }

  if (!evalData) {
    return <p className="py-6 text-[13px] text-muted-foreground">Carregando evals…</p>
  }

  return (
    <div className="space-y-4">
      <Card className="gap-4">
        <CardHeader>
          <CardTitle className="text-[14px] font-semibold">Lançar eval</CardTitle>
          <CardDescription className="text-[13px]">
            {shouldPollInitialSync ? 'Sincronizando artifacts externos...' : 'Session audit com LLM as a judge.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={submit}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="eval-from">De</Label>
                <Input id="eval-from" type="datetime-local" value={form.data.from_at} onChange={e => form.setData('from_at', e.target.value)} disabled={!canManage || form.processing} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="eval-to">Até</Label>
                <Input id="eval-to" type="datetime-local" value={form.data.to_at} onChange={e => form.setData('to_at', e.target.value)} disabled={!canManage || form.processing} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="eval-agent">Agente</Label>
                <Input id="eval-agent" value={form.data.agent_id} onChange={e => form.setData('agent_id', e.target.value)} disabled={!canManage || form.processing} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="eval-model">Modelo</Label>
                <Input id="eval-model" placeholder="padrão do OpenClaw" value={form.data.judge_model} onChange={e => form.setData('judge_model', e.target.value)} disabled={!canManage || form.processing} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="eval-request">Pedido do judge</Label>
              <Textarea id="eval-request" value={form.data.judge_request} onChange={e => form.setData('judge_request', e.target.value)} disabled={!canManage || form.processing} rows={3} />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="submit" disabled={!canManage || form.processing}>
                <Play className="h-4 w-4" />
                {form.processing ? 'Enfileirando...' : 'Lançar eval'}
              </Button>
              <Button type="button" variant="outline" onClick={() => { void refreshEvalRuns(true) }} disabled={refreshing}>
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Atualizando...' : 'Atualizar'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {refreshError && (
        <Alert variant="warning">
          <AlertTitle>Atualização indisponível</AlertTitle>
          <AlertDescription>{refreshError}</AlertDescription>
        </Alert>
      )}

      {runs.length === 0 ? (
        evalData.sync.pending ? (
          <p className="py-4 text-[13px] text-muted-foreground">Buscando evals salvas no OpenClaw…</p>
        ) : (
          <PanelEmptyState icon={FlaskConical} title="Nenhuma eval lançada ainda" description="A primeira aparece aqui." />
        )
      ) : (
        runs.map(run => <RunCard key={run.id} run={run} workerId={workerId} />)
      )}
    </div>
  )
}
